import { deepSignal } from 'deepsignal';
import { CHARITY } from '@util/const.js';
import { useEffect, useState } from 'preact/hooks';
import { formatColorName } from '@util/common.js';
import ColorPicker from '@components/pickers/color-picker/color.picker.jsx';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Description from '@components/overlays/shared/description.jsx';
import CharitySection from '@components/shared/charity-section.jsx';
import LinkSetup from '@components/overlays/shared/link-setup.jsx';
import {
  DonationTrainOverlay,
  DonationTrainOverlayBackgroundColors,
  toDonationTrainOverlayBackgroundColor,
} from '@playlive/overlay-core/overlays/donation-train';
import {
  getTrain,
  getTrainDonations,
  getTrainEnd,
} from '@util/donation-trains.js';

import { useRulesets } from './donation-train-overlay.hooks.js';
import { DonationTrainOverlayRulesets } from './donation-train-overlay-rulesets.jsx';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';
import useCommon from '@hooks/useCommon.js';
import YesNoInput from '@components/input/yes-no.input.jsx';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  donationTrainTheme: 'regular',
  donationTrainPosition: 'top',
  donationTrainMaxVisible: 1,
  donationTrainColor: 'default-red',
  donationTrainShowActivationMessage: 'yes',
});

export default function DonationTrainOverlayPage() {
  const { overlayInfo, overlayURL, setConfigState, tiltifyConfig } = useCommon({
    config,
    forceTiltify: true,
  });

  const [trains, setTrains] = useState([getTrain('1', '1', 1)]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newTrains = trains.map((train) => {
        train.donationCount += 1;
        train.lastDonation = new Date();
        train.trainEnd = getTrainEnd(5);
        train.donations.push(getTrainDonations('1', 1)[0]);
        return train;
      });

      setTrains(newTrains);
    }, 30000);

    return () => clearInterval(interval);
  });

  const { rulesets, createRuleset, updateRuleset, deleteRuleset } = useRulesets(
    tiltifyConfig.tiltifyCampaignID
  );

  function filterColors(color) {
    return DonationTrainOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay relative w-full min-h-40">
          <ScaledOverlayWrapper
            allowScaling={false}
            defaultScale={'scale-100'}
            innerClassName="h-[605px]"
          >
            <DonationTrainOverlay
              trains={trains}
              position={config.donationTrainPosition}
              theme={config.donationTrainTheme}
              maxTrainsVisible={config.donationTrainMaxVisible}
              demoMode
              onFactChange={() => console.log('fact change')}
              onTrainEnding={() => console.log('train ending')}
              onTrainEnded={() => console.log('train ended')}
              backgroundColor={toDonationTrainOverlayBackgroundColor(
                config.donationTrainColor
              )}
              status={'ACTIVE'}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper>
          <CustomizeSection title="Appearance">
            <span className="label-text mb-2">
              Background color:{' '}
              <b>{formatColorName(config.donationTrainColor)}</b>
            </span>

            <ColorPicker
              fieldName="donationTrainColor"
              currentColor={config.donationTrainColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <YesNoInput
              currentValue={config.donationTrainShowActivationMessage}
              name="donationTrainShowActivationMessage"
              onChange={setConfigState}
              mode="checkbox"
              label="Show Activation Message"
            >
              <span className="text-xs text-gray-500">
                {' '}
                Show 'Donate now to activate the donation train!' when there are
                no trains
              </span>
            </YesNoInput>
          </CustomizeSection>

          <CustomizeSection className="mt-8" title="Customize Time and Amounts">
            <DonationTrainOverlayRulesets
              updateRuleset={updateRuleset}
              createRuleset={createRuleset}
              deleteRuleset={deleteRuleset}
              rulesets={rulesets}
            />
          </CustomizeSection>
        </HorizontalCustomizeWrapper>
      </div>

      <Description>
        <p className="pb-3">
          The Donation Train includes a fun, interactive donation counter and
          timer. It encourages your viewers to donate specific amounts before
          the timer runs out, making the experience more engaging and exciting
          for everyone! To kick off a Donation Train, a specific number of
          donations of the same value need to be made within a set time, usually
          5 minutes. Once the train starts, your viewers need to keep donating
          that amount before the timer runs out. It's a fun way to keep the
          momentum going and engage your audience!
        </p>
      </Description>

      <CharitySection
        onChange={setConfigState}
        charityType={config.charityType}
      />

      <LinkSetup
        showCopyDemoModeLinkButton
        charityType={config.charityType}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        childrenSlot="stepTwo"
        childrenSlotPosition="after"
      />
    </div>
  );
}
